import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import SiteHeader from '../components/layout/SiteHeader';
import SiteLayout, { SiteMain } from '../components/layout/SiteLayout';
import PageHero from '../components/ui/PageHero';
import { ticketsAPI, paymentsAPI } from '../lib/api';

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('tickets');

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  // Fetch user tickets
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      const fetchTickets = async () => {
        try {
          const data = await ticketsAPI.getTicketsForUser(user.id);
          if (data.success) {
            setTickets(data.data.tickets || []);
          } else {
            setError('Failed to fetch tickets');
          }
        } catch (err) {
          setError('Failed to connect to server');
          console.error('Error fetching tickets:', err);
        } finally {
          setLoading(false);
        }
      };

      fetchTickets();
    }
  }, [isAuthenticated, user?.id]);

  useEffect(() => {
    if (!isAuthenticated || activeTab !== 'payments') return;

    const fetchPayments = async () => {
      setPaymentsLoading(true);
      try {
        const data = await paymentsAPI.getPaymentHistory();
        if (data?.success) {
          setPayments(data.data.payments || []);
        }
      } catch (err) {
        console.error('Error fetching payments:', err);
      } finally {
        setPaymentsLoading(false);
      }
    };

    fetchPayments();
  }, [isAuthenticated, activeTab]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-KE', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES'
    }).format(parseFloat(price));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'CONFIRMED':
        return 'bg-green-100 text-green-800';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED':
        return 'bg-green-100 text-green-800';
      case 'PROCESSING':
        return 'bg-blue-100 text-blue-800';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      case 'FAILED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (!isAuthenticated) {
    return null; // Will redirect to login
  }

  return (
    <SiteLayout className="bg-gray-50">
      <SiteHeader />

      <PageHero
        title="My dashboard"
        subtitle="Manage your tickets and profile"
        compact
      />

      <SiteMain>
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-lg mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab('tickets')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'tickets'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                My Tickets
              </button>
              <button
                onClick={() => setActiveTab('payments')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'payments'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Payments
              </button>
              <button
                onClick={() => setActiveTab('profile')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'profile'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Profile Settings
              </button>
            </nav>
          </div>
        </div>

        {/* Tickets Tab */}
        {activeTab === 'tickets' && (
          <div>
            {loading && (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading your tickets...</p>
              </div>
            )}

            {error && (
              <div className="text-center py-12">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
                  <p className="text-red-600 font-medium">{error}</p>
                </div>
              </div>
            )}

            {!loading && !error && tickets.length === 0 && (
              <div className="text-center py-12">
                <div className="bg-gray-50 rounded-lg p-8 max-w-md mx-auto">
                  <div className="text-6xl mb-4">🎫</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No Tickets Yet</h3>
                  <p className="text-gray-600 mb-4">You haven't purchased any tickets yet.</p>
                  <Link 
                    to="/events" 
                    className="bg-primary-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-600 transition-colors"
                  >
                    Browse Events
                  </Link>
                </div>
              </div>
            )}

            {!loading && !error && tickets.length > 0 && (
              <div className="grid gap-6">
                {tickets.map((ticket) => (
                  <div key={ticket.id} className="bg-white rounded-xl shadow-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-4">
                        <div className="w-16 h-16 bg-gradient-primary rounded-lg flex items-center justify-center">
                          <span className="text-2xl text-white">🎪</span>
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold text-gray-900">{ticket.event.title}</h3>
                          <p className="text-gray-600">{ticket.event.venue?.name}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`inline-flex px-3 py-1 rounded-md text-sm font-medium ${getStatusColor(ticket.status)}`}>
                          {ticket.status}
                        </span>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-4 gap-4 mb-4">
                      <div>
                        <p className="text-sm text-gray-500">Date</p>
                        <p className="font-medium">{formatDate(ticket.event.date)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Quantity</p>
                        <p className="font-medium">{ticket.quantity} ticket(s)</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Total Paid</p>
                        <p className="font-medium">{formatPrice(ticket.totalPrice)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">QR Code</p>
                        {ticket.qrCode && ticket.qrCode.startsWith('data:image') ? (
                          <img src={ticket.qrCode} alt="QR Code" className="w-16 h-16 border rounded" />
                        ) : (
                          <p className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">{ticket.qrCode}</p>
                        )}
                      </div>
                    </div>

                    <div className="flex justify-between items-center pt-4 border-t">
                      <div className="text-sm text-gray-500">
                        Purchased on {formatDate(ticket.createdAt)}
                      </div>
                      <div className="flex space-x-2">
                        <Link
                          to={`/tickets/${ticket.id}`}
                          className="bg-primary-500 text-white px-4 py-2 rounded-lg font-semibold hover:bg-primary-600 transition-colors"
                        >
                          View Details
                        </Link>
                        <button
                          type="button"
                          onClick={() => ticketsAPI.downloadTicketPdf(ticket.id)}
                          className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                        >
                          Download PDF
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <div>
            {paymentsLoading && (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4" />
                <p className="text-gray-600">Loading your payments...</p>
              </div>
            )}

            {!paymentsLoading && payments.length === 0 && (
              <div className="text-center py-12">
                <div className="bg-gray-50 rounded-lg p-8 max-w-md mx-auto">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No payments yet</h3>
                  <p className="text-gray-600 mb-4">Your M-Pesa payment history will appear here.</p>
                  <Link
                    to="/events"
                    className="bg-primary-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-600 transition-colors"
                  >
                    Browse events
                  </Link>
                </div>
              </div>
            )}

            {!paymentsLoading && payments.length > 0 && (
              <div className="grid gap-4">
                {payments.map((payment) => (
                  <div key={payment.id} className="bg-white rounded-xl shadow-lg p-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{payment.event?.title || 'Event'}</h3>
                        {payment.billRef && (
                          <p className="text-xs font-mono text-gray-400 mt-1">{payment.billRef}</p>
                        )}
                      </div>
                      <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium w-fit ${getPaymentStatusColor(payment.status)}`}>
                        {payment.status}
                      </span>
                    </div>
                    <div className="grid sm:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Amount</p>
                        <p className="font-medium">{formatPrice(payment.amount)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Tickets</p>
                        <p className="font-medium">{payment.quantity}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Date</p>
                        <p className="font-medium">{formatDate(payment.createdAt)}</p>
                      </div>
                      <div className="flex items-end">
                        {payment.status === 'PENDING' || payment.status === 'FAILED' ? (
                          <Link
                            to={`/checkout/${payment.id}`}
                            className="text-sm font-semibold text-primary-600 hover:text-primary-800"
                          >
                            Continue checkout →
                          </Link>
                        ) : payment.tickets?.[0] ? (
                          <Link
                            to={`/tickets/${payment.tickets[0].id}`}
                            className="text-sm font-semibold text-primary-600 hover:text-primary-800"
                          >
                            View ticket →
                          </Link>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Profile Settings</h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              {/* Profile Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                    <input
                      type="text"
                      value={user?.name || ''}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      readOnly
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                    <input
                      type="email"
                      value={user?.email || ''}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      readOnly
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                    <input
                      type="tel"
                      value={user?.phone || ''}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Add phone number"
                    />
                  </div>
                </div>
              </div>

              {/* Account Statistics */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Statistics</h3>
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Total Tickets</span>
                      <span className="text-2xl font-bold text-primary-500">{tickets.length}</span>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Total Spent</span>
                      <span className="text-2xl font-bold text-primary-500">
                        {formatPrice(tickets.reduce((sum, ticket) => sum + parseFloat(ticket.totalPrice), 0))}
                      </span>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Member Since</span>
                      <span className="text-lg font-medium text-gray-900">
                        {user?.createdAt ? formatDate(user.createdAt) : 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Account Actions</h3>
                  <p className="text-gray-600">Manage your account settings and preferences</p>
                </div>
                <div className="flex space-x-4">
                  <button className="bg-primary-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-600 transition-colors">
                    Update Profile
                  </button>
                  <button className="border border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors">
                    Change Password
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      </SiteMain>
    </SiteLayout>
  );
};

export default DashboardPage;
